import { memberSex } from '@/constants/memberBasicInfo';
import { emailTypes, phoneTypes } from '@/constants/memberContactInfo';
import { AddressType } from '@/types/address';
import { AffectUser } from '@/types/affectUser';
import { Classification } from '@/types/classification';
import type { EpisodeOfCare } from '@/types/episodeOfCare';
import type { ExternalVerification } from '@/types/externalVerification';
import type { InsurancePolicy } from '@/types/insurancePolicy';
import { Jurisdiction } from '@/types/jurisdiction';
import type { LeadSource } from '@/types/leadSource';
import PersonPersonRelationship from '@/types/personPersonRelationship';
import PersonStaffRelationship from '@/types/personStaffRelationship';
import { Program } from '@/types/program';

import type { AppointmentAttendance } from './appointmentAttendance';
import type { AppointmentAttendanceFeedback } from './appointmentAttendanceFeedback';
import type { ChartedDiagnosis } from './chartedDiagnosis';

export interface PersonExternalLink {
  homebase: ExternalLink;
  iterable: ExternalLink | null;
  healthie: ExternalLink | null;
  mixpanel: ExternalLink | null;
}

export interface ExternalLink {
  id: string;
  url: string;
}

export interface EmergencyContact {
  id?: string;
  person_id: string;
  name?: string | null;
  relationship?: string | null;
  phone?: string | null;
  person_to_contact?: PersonMinimal | null;
  person_to_contact_id?: string | null;
}

export interface PersonSimple {
  id: string;
  external_links: PersonExternalLink; // Assuming this is an existing interface or type
  first_name: string;
  last_name: string;
}
export interface Phone {
  id?: string;
  default: boolean;
  number: string;
  kind: (typeof phoneTypes)[number];
}

export interface Email {
  id?: string;
  default: boolean;
  address: string;
  type: (typeof emailTypes)[number];
}

export interface Address {
  address1?: string | null;
  address2?: string | null;
  address_type?: AddressType | null;
  address_type_id?: string | null;
  city?: string | null;
  country?: string;
  default?: boolean;
  delivery_point_barcode?: string | null;
  dpv_match_code?: string | null;
  first_name?: string | null;
  id?: string;
  last_name?: string | null;
  mailing_instructions?: string | null;
  person_id?: string; // write only
  postal_code?: string;
  state?: string | null;
}

export interface Settings {
  id: string;
  channel_sms_enabled: boolean;
  notifications: boolean;
  appointment_reminder_notifications: boolean;
  challenge_notifications: boolean;
  document_notifications: boolean;
  community_message_notifications: boolean;
}

export type Pronouns = string;

export interface Demographics {
  birthday?: string | null;
  race_ethnicity?: string[] | null;
  race_ethnicity_other?: string | null;
  hispanic_origin?: boolean | null;
  housing_status?: string | null;
  housing_status_other?: string | null;
  employment_status?: string | null;
  gender?: string | null;
  sex?: (typeof memberSex)[number] | '' | null;
  pronouns?: Pronouns | null;
  transgender?: string | null;
  sexual_orientation?: string | null;
  education?: string | null;
  resides_with_other_drug_users?: boolean | null;
  relationship_status?: string | null;
  asam_score?: number | null;
  nps_score?: number | null;
  goal_description?: string | null;
  experience_in_treatment?: string[] | null;
  opiod_use_oxy?: string | null;
  opiod_use_heroin_fentanyl?: string | null;
}

export interface PersonMergeTarget {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface Person {
  active_classifications: Classification[];
  addresses: Address[];
  app_login_email?: string | null;
  attributes_missing_for_healthie: string[];
  birthday?: string | null;
  candid_patient_id?: string | null;
  cashout_enabled: boolean;
  challenges: Challenges;
  classifications: string[];
  deceased_on?: string | null;
  deleted_at?: string | null;
  merged_into?: PersonMergeTarget | null;
  default_currency: CurrencyChoices | CurrencyChoices.USD;
  default_email?: string;
  default_phone?: string;
  demographics?: Demographics | null;
  emails: Email[];
  episode_of_care: EpisodeOfCare | null;
  exclude_from_analytics: boolean;
  exclude_from_cm_payments: boolean;
  external_links: PersonExternalLink;
  first_name?: string | null;
  healthie_id: number | null;
  id: string;
  insurance_policies: InsurancePolicy[];
  has_run_discovery?: boolean;
  most_recent_insurance_discovery?: ExternalVerification | null;
  iop_counselor: Staff | null;
  iop_counselor_id?: string | null; // write only
  jurisdiction?: Jurisdiction;
  jurisdiction_id?: string | null;
  kustomer_id: string | null;
  last_app_open_at?: string;
  last_known_app_version?: string;
  last_known_system?: MobileSystem;
  last_name?: string | null;
  last_intake_appointment_attended: AppointmentAttendance | null;
  last_non_breathalyzer_appointment_attended: AppointmentAttendance | null;
  last_program_adherence_appointment_attended: AppointmentAttendance | null;
  links?: string;
  medical_record_number?: string | null;
  member_advocate: Staff | null;
  member_advocate_id?: string | null; // write only
  is_reward_card_eligible: boolean | true;
  most_recent_attendance_feedback?: AppointmentAttendanceFeedback | null;
  most_recent_chart_diagnoses?: ChartedDiagnosis | null;
  next_appointment_attendance?: AppointmentAttendance | null;
  nickname?: string | null;
  payment_type?: string | null;
  peer_supporter: Staff | null;
  peer_supporter_id?: string | null; // write only
  pending_payout_in_cents: number;
  pex_enabled: boolean;
  phones: Phone[];
  potential_duplicates: PersonPotentialDuplicate[];
  profile_image_url?: string;
  program?: Program;
  program_qualification?: ProgramQualification | null;
  pronouns?: Pronouns | null;
  recovery_goal?: string;
  referrer_name?: string | null;
  user_identified_lead_source?: LeadSource | null;
  user_identified_lead_source_id?: string | null; // write only
  related_person_relationships: PersonPersonRelationship[];
  settings: Settings;
  sex?: (typeof memberSex)[number] | '' | null;
  staff_relationships: PersonStaffRelationship[];
  person_relationships: PersonPersonRelationship[];
  subscribed_to_warming: boolean;
  tags: string[];
  targeted_substances: string[];
  time_zone?: string | null;
  treatment_week_and_day: [week: number, day: number];
  user: AffectUser | null;
  user_terminated_account: boolean;
  warming_sequence: number;
  warming_sequence_label: string | null;
  active_pending_discharge: boolean;
  emergency_contact?: EmergencyContact | null;
}

export interface PersonMinimal {
  id: string;
  first_name: string | null;
  last_name?: string | null;
  nickname?: string | null;
  profile_image_url?: string;
  pronouns?: Pronouns | null;
  time_zone?: string | null;
  birthday?: string | null;
  jurisdiction_name?: string | null;
  channel_sms_enabled?: boolean;
}

export interface PersonPotentialDuplicate {
  birthday: string | null;
  default_email?: string | null;
  default_phone?: string | null;
  external_links: PersonExternalLink;
  first_name: string | null;
  id: string;
  jurisdiction: string | null;
  last_name?: string | null;
  profile_image_url?: string | null;
}

export interface StaffFilters {
  kustomer_id__isnull?: boolean;
  healthie_id__isnull?: boolean;
  time_zone?: string | null;
  primary_role__slug?: string | null;
  credentials?: string | null;
}

export interface Staff {
  email?: string | null;
  first_name?: string;
  healthie_id?: number | null;
  id: string;
  last_name?: string;
  avatar_url?: string | null;
  clinical_supervisor?: Staff | null;
}

export interface StaffForBooking extends Staff {
  is_credentialed: boolean;
}

export interface Challenges {
  last_challenge_finished_at: string;
  num_challenges_available: number;
  num_challenges_completed: number;
}

export interface ProgramQualification {
  _program_id?: string | null;
  created_at: string;
  default_program: Program | null;
  disqualification_reasons: string[];
  ended_at: string | null;
  id: string;
  intake_appointment_attendance: AppointmentAttendance | null;
  intake_rejection_note: string | null;
  intake_rejection_reasons: string[];
  primary_substance: string | null;
  rejected_at: string | null;
  rejected_by: AffectUser | null;
  selected_program: Program | null;
  status: string;
  treatment_preference: string | null;
}

export interface InsuranceCard {
  front: string;
  back: string;
}

export enum CurrencyChoices {
  USD = 'USD',
  EXP = 'EXP'
}

export enum MobileSystem {
  IOS = 'ios',
  ANDROID = 'android'
}

export type PaymentTypeChoice = [string, string];
